/**
 * PRODUCT_DEV-1239 顧客番号（企業マスタ systemIntegrationId）の呼び名。
 *
 * テナントごとに「全店顧客番号」などの呼び名を設定でき、番号を出す画面と CSV 取り込みの列名に使う。
 * 効くのは機能フラグ「全店顧客番号入力」（companyExternalSystemId）と
 * 「顧客番号の呼び名と番号検索」（customerNumberLabel）が両方 ON のときだけ。
 * それ以外、または呼び名が未設定なら、画面ごとの既定の名前で出す。
 *
 * 判定を画面・API で1か所にそろえるため、ここに置く（片方だけ直すと
 * 「設定したのに画面に出ない」「CSV だけ古い名前を求める」が起きる）。
 */
import { isValidTenantLabel, normalizeTenantLabel } from "../lib/tenantBranding";

/** 呼び名の長さ上限（入力欄のラベル・CSV の列名に収まる範囲） */
export const CUSTOMER_NUMBER_LABEL_MAX = 20;

/** 呼び名が未設定のときの名前（企業マスタ・CSV・商談画面・候補） */
export const DEFAULT_CUSTOMER_NUMBER_LABEL = "外部システム企業ID";

/** マイ顧客の編集画面だけは、もともとの既定名がこちら */
export const DEFAULT_CUSTOMER_NUMBER_LABEL_MY_CUSTOMER = "外部システムID";

/** 2つのフラグの名前（判定の正本） */
export const CUSTOMER_NUMBER_FLAG_KEYS = [
  "companyExternalSystemId",
  "customerNumberLabel",
] as const;

/** 呼び名と番号検索が使えるか。`isOn` にはそのテナントのフラグ判定を渡す */
export const isCustomerNumberLabelEnabled = (
  isOn: (flagKey: string) => boolean,
): boolean => CUSTOMER_NUMBER_FLAG_KEYS.every((key) => isOn(key));

/** 空は null（＝未設定） */
export const normalizeCustomerNumberLabel = (
  raw: string | null | undefined,
): string | null => normalizeTenantLabel(raw);

/**
 * CSV の列名にそのまま使うので入れられない文字（区切りの「,」と囲みの「"」）。
 * 入ると見出しが崩れ、そのテナントの取り込みが一切通らなくなる。改行は制御文字として別に弾く。
 *
 * PRODUCT_DEV-1344: 先頭の `=` `+` `-` `@` も入れられない（先頭の空白・タブ・CR は飛ばして見る）。取り込みのひな形 CSV の
 * 見出しにそのまま入るので、`=cmd|…` のような値を Excel が数式として開く（決めるのはパワーユーザー、開くのは管理者）。
 * 途中の `-` `@`（「顧客番号-本店」）はよい。
 */
export const CUSTOMER_NUMBER_LABEL_FORBIDDEN = /[,"]|^\s*[=+\-@]/;

/** 保存できない呼び名のときの説明（画面と API で同じ文を出す） */
export const CUSTOMER_NUMBER_LABEL_FORBIDDEN_MESSAGE =
  "「,」「\"」と、先頭の「=」「+」「-」「@」は使えません（CSV 取り込みの列名に使うため）";

/** 保存してよい呼び名か（空は未設定として通す） */
export const isValidCustomerNumberLabel = (
  raw: string | null | undefined,
): boolean =>
  isValidTenantLabel(raw, CUSTOMER_NUMBER_LABEL_MAX) &&
  !CUSTOMER_NUMBER_LABEL_FORBIDDEN.test(normalizeCustomerNumberLabel(raw) ?? "");

/**
 * 画面・CSV に出す名前を決める。使えない（フラグが揃っていない）か未設定なら `fallback`。
 */
export const resolveCustomerNumberLabel = (
  enabled: boolean,
  label: string | null | undefined,
  fallback: string = DEFAULT_CUSTOMER_NUMBER_LABEL,
): string => {
  if (!enabled) return fallback;
  const normalized = normalizeCustomerNumberLabel(label);
  // PRODUCT_DEV-1344: 規則を広げる前に保存された値がまだ数式の形なら、CSV の見出しに出さず既定名に戻す
  if (normalized === null || CUSTOMER_NUMBER_LABEL_FORBIDDEN.test(normalized)) return fallback;
  return normalized;
};
